import { useState } from 'react';
import { resolveImagePath } from "@/utils/imageResolver";
import { cn } from "@/utils/cn";

export interface NavigationItem {
  label: string;
  href: string;
  submenu?: { label: string; href: string }[];
}

const navigationItems: NavigationItem[] = [
  { 
    label: 'Home', 
    href: '/' 
  },
  {
    label: 'Pay Online',
    href: '/shop',
    submenu: [
      { label: 'Fly-In Store', href: '/flyinstore' },
      { label: 'Monthly Plans', href: '/monthly-plans' }
    ]
  },
  {
    label: 'Join Us',
    href: '/blank-page-1',
    submenu: [
      { label: 'Membership', href: '/blank-page' }
    ]
  },
  {
    label: 'Flight School',
    href: 'https://learn2flyct.com/'
  },
  {
    label: 'Visitor Info',
    href: '/visitor-info',
    submenu: [
      { label: 'Restaurants', href: '/restaurants' },
      { label: 'Accommodations', href: '/accomodations' },
      { label: 'Getting Around', href: '/getting-around' }
    ]
  },
  {
    label: 'Pilot Info',
    href: '/pilot-info-1',
    submenu: [
      { label: 'Hangars', href: '/hangars' },
      { label: 'Fuel', href: '/fuel' }
    ]
  },
  {
    label: 'Airport FAQ',
    href: '/airportfaq'
  },
  {
    label: 'Services & Organizations',
    href: '/services-organizations'
  },
  {
    label: 'History',
    href: '/history'
  },
  {
    label: 'About',
    href: '/about'
  },
  {
    label: 'Followers',
    href: '/followers'
  }
];

interface HeaderProps {
  className?: string;
}

export function Header({ className }: HeaderProps) {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const handleDropdownToggle = (itemLabel: string) => {
    setOpenDropdown(openDropdown === itemLabel ? null : itemLabel);
  };

  const handleLinkClick = () => {
    setOpenDropdown(null);
  };

  return (
    <header className={cn("bg-white shadow-sm border-b border-gray-100", className)}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <div className="flex-shrink-0">
            <a href="/" className="flex items-center">
              <img
                src={resolveImagePath("/images/logo.png")}
                alt="Simsbury Airport Logo"
                className="h-16 w-auto"
              />
            </a>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex space-x-8">
            {navigationItems.map((item) => (
              <div key={item.label} className="relative">
                {item.submenu ? (
                  <div className="relative">
                    <button
                      onClick={() => handleDropdownToggle(item.label)}
                      className="text-gray-700 hover:text-blue-600 px-3 py-2 text-sm font-medium transition-colors duration-200 flex items-center"
                    >
                      {item.label}
                      <svg
                        className={cn(
                          "ml-1 h-4 w-4 transition-transform duration-200",
                          openDropdown === item.label && "rotate-180"
                        )}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {openDropdown === item.label && (
                      <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                        <div className="py-1">
                          {item.submenu.map((subItem) => (
                            <a
                              key={subItem.label}
                              href={subItem.href}
                              onClick={handleLinkClick}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors duration-150"
                            >
                              {subItem.label}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <a
                    href={item.href}
                    target={item.href.startsWith('http') ? '_blank' : undefined}
                    rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="text-gray-700 hover:text-blue-600 px-3 py-2 text-sm font-medium transition-colors duration-200"
                  >
                    {item.label}
                  </a>
                )}
              </div>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <div className="lg:hidden">
            <button
              onClick={() => handleDropdownToggle('mobile')}
              className="p-2 rounded-md text-gray-700 hover:text-blue-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
              aria-label="Toggle mobile menu"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {openDropdown === 'mobile' ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {openDropdown === 'mobile' && (
          <div className="lg:hidden border-t border-gray-200 pt-4 pb-4">
            <div className="space-y-1">
              {navigationItems.map((item) => (
                <div key={item.label}>
                  {item.submenu ? (
                    <div>
                      <button
                        onClick={() => handleDropdownToggle(`mobile-${item.label}`)}
                        className="w-full text-left block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 transition-colors duration-150"
                      >
                        <div className="flex items-center justify-between">
                          {item.label}
                          <svg
                            className={cn(
                              "h-4 w-4 transition-transform duration-200",
                              openDropdown === `mobile-${item.label}` && "rotate-180"
                            )}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </button>
                      
                      {openDropdown === `mobile-${item.label}` && (
                        <div className="pl-6 space-y-1">
                          {item.submenu.map((subItem) => (
                            <a
                              key={subItem.label}
                              href={subItem.href}
                              onClick={handleLinkClick}
                              className="block px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-gray-50 transition-colors duration-150"
                            >
                              {subItem.label}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <a
                      href={item.href}
                      target={item.href.startsWith('http') ? '_blank' : undefined}
                      rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                      onClick={handleLinkClick}
                      className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 transition-colors duration-150"
                    >
                      {item.label}
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}